import { useState } from "react"

import { BrandInitialMonogram } from "@/components/brand-initial-monogram"
import { SPLASH_COVER_ASPECT_CLASS } from "@/lib/splash-cover"
import { cn } from "@/lib/utils"

/**
 * OKS-857 — Portrait (9:16) preview band for the brand splash image.
 *
 * The 9:16 sibling of `BrandCoverBand`'s `default` (3:2) variant, used by
 * `BrandSplashCoverImageUploader` to preview the portrait splash image in
 * Brand Settings → Profile. `BrandCoverBand` is single-sourced to 3:2 and
 * guarded by a parity test, so rather than overriding its ratio (which
 * would trip the guard) OKS-857 ships this dedicated 9:16 band and
 * single-sources its ratio in `lib/splash-cover.ts` (TRD KD-5).
 *
 *   - `coverUrl` non-null string → renders the image at
 *     `SPLASH_COVER_ASPECT_CLASS object-cover` (9:16) with `loading="eager"`.
 *   - `coverUrl` null OR the image fails to load → dark band + centred
 *     monogram fallback (a splash-image-less brand is a valid default; the
 *     splash itself then falls back to the landscape cover / dark backdrop).
 *
 * `onImageLoad` / `onImageError` are BOTH forwarded so a consumer can settle
 * an edit-load skeleton either way — a 404 on a stale signed URL fires only
 * `onError`, never `onLoad`, so a load-only gate would strand the skeleton
 * (the OKS-855 bug this component avoids by construction).
 *
 * Brand isolation: takes the resolved cover URL + brand name directly as
 * props; there is no global store. Consumers pass server-resolved
 * (`Current.brand`) values.
 */
export interface BrandSplashCoverBandProps {
  /** Signed splash-image URL from the server, or null when none attached. */
  coverUrl: string | null | undefined
  /** Brand display name — drives the dark-fallback monogram initial. */
  brandName: string | null | undefined
  /** Tailwind extras (rounded corners, max-width caps). */
  className?: string
  /** Test id forwarded for assertion contracts. Defaults to
   *  "brand-splash-cover-band". */
  testId?: string
  /** Forwarded to the image `<img>`'s `onLoad` — fires ONLY on the image
   *  branch (never the dark fallback). */
  onImageLoad?: () => void
  /** Forwarded to the image `<img>`'s `onError` — fires alongside the
   *  band's own degrade-to-dark-fallback. Lets a consumer settle a skeleton
   *  when a stale signed URL 404s (fires `onError`, never `onLoad`). */
  onImageError?: () => void
}

export function BrandSplashCoverBand({
  coverUrl,
  brandName,
  className,
  testId = "brand-splash-cover-band",
  onImageLoad,
  onImageError,
}: BrandSplashCoverBandProps) {
  // Track image-load failure so a stale signed URL or mid-render purge
  // gracefully degrades to the dark-fallback band instead of the browser's
  // broken-image icon.
  const [imageBroken, setImageBroken] = useState(false)

  const handleImageError = () => {
    setImageBroken(true)
    onImageError?.()
  }

  const showImage = Boolean(coverUrl) && !imageBroken
  const monogramName = (brandName ?? "").trim() || "?"

  if (showImage) {
    return (
      <div
        data-testid={testId}
        data-state="image"
        className={cn(
          SPLASH_COVER_ASPECT_CLASS,
          "bg-surface-inverse relative w-full overflow-hidden",
          className,
        )}
      >
        <img
          src={coverUrl ?? undefined}
          alt=""
          loading="eager"
          decoding="async"
          onError={handleImageError}
          onLoad={onImageLoad}
          data-testid={`${testId}-image`}
          className="size-full object-cover"
        />
      </div>
    )
  }

  // Empty / failed-load fallback — dark 9:16 band + centred monogram.
  return (
    <div
      data-testid={testId}
      data-state="fallback"
      className={cn(
        SPLASH_COVER_ASPECT_CLASS,
        "bg-surface-inverse relative w-full overflow-hidden",
        "flex items-center justify-center",
        className,
      )}
    >
      <BrandInitialMonogram
        name={monogramName}
        className="size-20 bg-white/10 text-3xl text-white"
        testId={`${testId}-fallback`}
      />
    </div>
  )
}

export default BrandSplashCoverBand
